import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ImmerReducer, useImmerReducer } from "use-immer";
import { useIdleTimer } from "react-idle-timer";

import {
  queryClient,
  setUnauthorizedHandler,
  useCollection,
  useResource,
} from "@/hooks/resources";
import { storage } from "@/utils/storage";
import { mediaCache } from "@/utils/media-cache";
import ChildAuthorizationType from "@/types/child-authorization-type";
import UserType from "@/types/user-type";

const IDLE_LOGOUT_MS = 10 * 60 * 1000; // 10 minutes

type AppContextType = {
  state: AppState;
  handlers: AppHandlers;
  actions: Record<Exclude<keyof typeof actions, "clearSession">, (value: string) => Promise<void>> & Record<"clearSession", () => Promise<void>>;
};

const AppContext = createContext<AppContextType | null>(null);

type AppState = ReducerState & {
  isLoading: boolean;
  childAuthorizations: ChildAuthorizationType[];
  isLoadingChildAuthorizations: boolean;
  currentUser?: UserType;
  isLoadingCurrentUser: boolean;
  hasActiveSubscription: boolean;
};

type ReducerState = {
  authToken?: string;
  isLoadingAuthToken: boolean;
  selectedChildId?: string;
  isLoadingSelectedChildId: boolean;
};

type AppHandlers = {
  handleSelectChildId: (event: React.ChangeEvent<HTMLSelectElement>) => void;
};

const actions = {
  setAuthToken: "set_auth_token",
  setSelectedChildId: "set_selected_child_id",
  clearSession: "clear_session",
} as const;

type AppAction = {
  type:
    | (typeof actions)[keyof typeof actions]
    | "set_initial_values_from_storage";
  payload?: unknown;
};

export const AppProvider = ({ children }: PropsWithChildren) => {
  const reducer: ImmerReducer<ReducerState, AppAction> = (state, action) => {
    switch (action.type) {
      case actions.setAuthToken:
        state.authToken = action.payload as string;
        break;

      case actions.setSelectedChildId:
        state.selectedChildId = action.payload as string;
        break;

      case actions.clearSession:
        state = { isLoadingAuthToken: false, isLoadingSelectedChildId: false }
        break;

      case "set_initial_values_from_storage": {
        const { selectedChildId, authToken } = action.payload as {
          selectedChildId: string;
          authToken: string;
        };

        state.selectedChildId ??= selectedChildId;
        state.authToken = authToken;
        state.isLoadingAuthToken = false;
        state.isLoadingSelectedChildId = false;
        break;
      }
    }

    return state;
  };

  const [state, dispatch] = useImmerReducer(reducer, {
    isLoadingAuthToken: true,
    isLoadingSelectedChildId: true,
  });

  const [childAuthorizations, { isLoading: isLoadingChildAuthorizations }] =
    useCollection<ChildAuthorizationType>("/child_authorizations", {
      refetchOnMount: false,
      enabled: !!state.authToken,
    });

  const [currentUser, { isLoading: isLoadingCurrentUser }] =
    useResource<UserType>("/users/me", {
      refetchOnMount: false,
      enabled: !!state.authToken,
    });

  // Auto-select child when there's only one
  useEffect(() => {
    if (!isLoadingChildAuthorizations && childAuthorizations?.length === 1 && !state.selectedChildId) {
      setSelectedChildId(String(childAuthorizations?.[0].child.id));
    }
  }, [isLoadingChildAuthorizations, childAuthorizations, state.selectedChildId]);

  const handleSelectChildId = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedChildId(event.target.value);
  };

  const setInitialValuesFromStorage = useCallback(async () => {
    const selectedChildId = await storage.get("selectedChildId");
    const authToken = await storage.get("authToken");

    dispatch({
      type: "set_initial_values_from_storage",
      payload: { selectedChildId, authToken },
    });
  }, [dispatch]);

  useEffect(() => {
    setInitialValuesFromStorage();
  }, [setInitialValuesFromStorage]);

  const setAuthToken = async (authToken: string) => {
    // Clear media cache when user logs in (new login or re-login)
    await mediaCache.clearCache();
    
    await storage.set("authToken", authToken);

    dispatch({ type: actions.setAuthToken, payload: authToken });
  };

  const setSelectedChildId = async (childId: string) => {
    await storage.set("selectedChildId", childId);

    dispatch({ type: actions.setSelectedChildId, payload: childId });
  };

  const clearSession = useCallback(async () => {
    await storage.clear();
    queryClient.clear();

    // Clear media cache when user logs out
    await mediaCache.clearCache();

    dispatch({ type: actions.clearSession });
  }, [dispatch]);

  // The API rejected the stored token (expired, revoked, or signed out
  // elsewhere). Drop the session so the app redirects to sign-in instead of
  // leaving the user staring at a page whose data will never arrive.
  useEffect(() => {
    setUnauthorizedHandler(clearSession);

    return () => setUnauthorizedHandler(undefined);
  }, [clearSession]);

  // Auto-logout after 10 minutes of inactivity (only when logged in)
  useIdleTimer({
    timeout: IDLE_LOGOUT_MS,
    onIdle: () => {
      if (state.authToken) {
        clearSession();
      }
    },
    disabled: !state.authToken,
    crossTab: true,
    syncTimers: 200,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "visibilitychange",
    ],
  });

  const isLoading = useMemo(
    () =>
      state.isLoadingAuthToken ||
      state.isLoadingSelectedChildId ||
      isLoadingChildAuthorizations,
    [
      state.isLoadingAuthToken,
      state.isLoadingSelectedChildId,
      isLoadingChildAuthorizations,
    ],
  );

  const hasActiveSubscription = useMemo(
    () => currentUser?.activeSubscriptions.length != null && currentUser?.activeSubscriptions.length > 0,
    [currentUser],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider
        value={{
          state: {
            ...state,
            isLoading,
            childAuthorizations: childAuthorizations || [],
            isLoadingChildAuthorizations,
            currentUser,
            isLoadingCurrentUser,
            hasActiveSubscription,
          },
          handlers: {
            handleSelectChildId,
          },
          actions: {
            setAuthToken,
            setSelectedChildId,
            clearSession,
          },
        }}
      >
        {children}
      </AppContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
    </QueryClientProvider>
  );
};

export const useAppContext = () => {
  const appContext = useContext(AppContext);

  return appContext as AppContextType;
};
